import { ErrorMessage } from '@hookform/error-message';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, get, useFormContext } from 'react-hook-form';
import { useTownByZipCode } from '@workspace/api';
import { Field, Input, Label, SelectNative, TextError } from '../form';

export interface CodePostalVilleProps {
  label?: string;
  nameCP: string;
  nameVille: string;
  disabled?: boolean;
  customValidation?: (value: string) => string | undefined;
  zonier?: 'belair' | 'wakam';
}

/**
 * Champ code postal + ville connecté avec API
 * - Récupère automatiquement les villes à partir du code postal
 * - Validation personnalisable via customValidation
 * - Sélection unique ou multiple selon le nombre de villes trouvées
 */
export function FieldCodePostalVille({ label, nameCP, nameVille, disabled, customValidation, zonier = 'belair' }: CodePostalVilleProps) {
  const methods = useFormContext();
  const watchedCP = methods.watch(nameCP);
  const queryCP = useTownByZipCode(watchedCP, zonier);

  const options = useMemo(
    () =>
      queryCP.data?.data?.map((town: any) => ({
        value: town.toUpperCase(),
        label: town.toUpperCase(),
      })) || [],
    [queryCP.data],
  );

  const handleTownsData = useCallback(() => {
    if (!queryCP.isSuccess || !queryCP.data?.data) return;

    // Si la validation personnalisée refuse ce code postal, ne pas traiter la réponse API
    if (customValidation && watchedCP) {
      const validationResult = customValidation(watchedCP);
      if (validationResult) {
        return;
      }
    }

    if (queryCP.data.data.length > 0) {
      // Auto-sélection de la première ville (en majuscules)
      const townName = queryCP.data.data[0].toUpperCase();
      const currentVille = methods.getValues(nameVille);

      // Définir la première ville si aucune ville n'est sélectionnée ou si la ville actuelle n'est plus valide
      if (!currentVille || !queryCP.data.data.some((town: string) => town.toUpperCase() === currentVille)) {
        methods.setValue(nameVille, townName, { shouldValidate: true });
      }
    } else {
      methods.setError(nameCP, {
        type: 'notAllowed',
        message: 'Aucune ville trouvée',
      });
      methods.setValue(nameVille, '', { shouldValidate: true });
    }
  }, [queryCP.isSuccess, queryCP.data, watchedCP, customValidation, methods, nameCP, nameVille]);

  useEffect(() => {
    handleTownsData();
  }, [handleTownsData]);

  // Réinitialiser la ville quand le code postal change (via watch)
  useEffect(() => {
    // On ne reset que si le CP a une longueur invalide ou vide
    if (watchedCP && watchedCP.length < 5) {
      const currentVille = methods.getValues(nameVille);
      if (currentVille) {
        methods.setValue(nameVille, '', { shouldValidate: false });
      }
    }
  }, [watchedCP, methods, nameVille]);

  return (
    <Field>
      <Label required>{label}</Label>
      <div className="flex gap-1">
        <Input
          className="max-w-36"
          loading={queryCP.isLoading}
          placeholder="75000"
          type="number"
          {...methods.register(nameCP, {
            required: { value: true, message: 'Ce champs est requis' },
            pattern: { value: /^\d{5}$/, message: 'Code postal invalide' },
            validate: customValidation ? { customValidation } : undefined,
            onChange: (e) => {
              const inputValue = e.target.value;
              if (inputValue.length > 5) {
                e.target.value = inputValue.slice(0, 5);
                methods.setValue(nameCP, inputValue.slice(0, 5));
              }
            },
          })}
          error={get(methods.formState.errors, nameCP)}
          disabled={disabled}
        />

        <Controller
          name={nameVille}
          control={methods.control}
          rules={{
            required: { value: true, message: 'Ce champs est requis' },
          }}
          render={({ field }) => (
            <SelectNative
              {...field}
              value={field.value || ''}
              disabled={disabled || options.length === 0}
              error={get(methods.formState.errors, nameVille)}
              options={options}
              placeholder="Sélectionnez une ville"
            />
          )}
        />
      </div>

      <ErrorMessage
        errors={methods.formState.errors}
        name={nameCP}
        render={({ message }) => <TextError>{message}</TextError>}
      />
    </Field>
  );
}
